import { useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import { useHistory, useLocation } from "react-router-dom"
import Brand from "../components/Brand"
import LegalConsent from "../components/LegalConsent"
import { useToast } from "../context/ToastContext"
import apiClient from "../helpers/apiClient"
import { getHeaderTheme } from "../config/headerTheme"

// react-router-dom v5 no expone useSearchParams; este hook replica ese comportamiento.
const useSearchParams = () => {
  const location = useLocation()
  return useMemo(() => new URLSearchParams(location.search), [location.search])
}

const OnboardingPage = () => {
  const history = useHistory()
  const searchParams = useSearchParams()
  const toast = useToast()
  const api = apiClient()
  const [loading, setLoading] = useState(false)
  const [acceptedLegal, setAcceptedLegal] = useState(false)
  const [form, setForm] = useState({
    adminName: "",
    adminEmail: "",
    restaurantName: "",
    restaurantAddress: "",
    restaurantCountry: "",
    hostFullName: "",
    hostEmail: "",
  })

  useEffect(() => {
    const nextForm = {
      adminName: searchParams.get("adminName") || "",
      adminEmail: searchParams.get("adminEmail") || "",
      restaurantName: searchParams.get("restaurantName") || "",
      restaurantAddress: searchParams.get("restaurantAddress") || "",
      restaurantCountry: searchParams.get("restaurantCountry") || "",
      hostFullName: `${searchParams.get("hostName") || ""} ${searchParams.get("hostLastName") || ""}`.trim(),
      hostEmail: searchParams.get("hostEmail") || "",
    }
    console.log("[Onboarding] Query params loaded:", nextForm)
    setForm(nextForm)
  }, [searchParams])

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const splitAdminName = (value = "") => {
    const parts = String(value).trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return { name: "", lastName: "" }
    if (parts.length === 1) return { name: parts[0], lastName: "" }
    return {
      name: parts[0],
      lastName: parts.slice(1).join(" "),
    }
  }

  const splitFullName = (value = "") => {
    const parts = String(value).trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return { name: "", lastName: "" }
    if (parts.length === 1) return { name: parts[0], lastName: "" }
    return {
      name: parts[0],
      lastName: parts.slice(1).join(" "),
    }
  }

  const buildFallbackResto = () => {
    const safeSlug = `${form.restaurantName}-${form.restaurantCountry}`
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "resto-onboarding"

    return {
      name: form.restaurantName.trim(),
      formatted_address: form.restaurantAddress.trim(),
      address: form.restaurantAddress.trim(),
      country: form.restaurantCountry.trim(),
      place_id: `onboarding-${safeSlug}`,
    }
  }

  const handleSubmit = async () => {
    if (loading) return
    console.log("[Onboarding] Submit started", form)

    if (!form.adminName || !form.adminEmail || !form.restaurantName || !form.hostFullName || !form.hostEmail) {
      console.log("[Onboarding] Validation failed: missing required fields")
      toast.error("Completa administrador, restaurante y datos del host.")
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(form.adminEmail)) {
      console.log("[Onboarding] Validation failed: invalid admin email", form.adminEmail)
      toast.error("Ingresa un correo del administrador válido.")
      return
    }
    if (!emailRegex.test(form.hostEmail)) {
      console.log("[Onboarding] Validation failed: invalid host email", form.hostEmail)
      toast.error("Ingresa un correo del host válido.")
      return
    }
    if (!acceptedLegal) {
      toast.error("Debes aceptar los términos del servicio para restaurantes y la política de privacidad.")
      return
    }

    try {
      setLoading(true)
      const admin = splitAdminName(form.adminName)
      const host = splitFullName(form.hostFullName)
      const fallbackResto = buildFallbackResto()
      console.log("[Onboarding] Admin split result:", admin)
      console.log("[Onboarding] Host split result:", host)
      console.log("[Onboarding] Fallback resto built:", fallbackResto)

      if (!admin.lastName) {
        console.log("[Onboarding] Validation failed: admin lastName missing after split")
        toast.error("Ingresa nombre y apellido del administrador.")
        setLoading(false)
        return
      }
      if (!host.lastName) {
        console.log("[Onboarding] Validation failed: host lastName missing after split")
        toast.error("Ingresa nombre y apellido del host.")
        setLoading(false)
        return
      }

      const restoPayload = {
        name: admin.name,
        lastName: admin.lastName,
        email: form.adminEmail.trim(),
        resto: fallbackResto,
        type: "resto",
      }
      console.log("[Onboarding] Creating resto with payload:", restoPayload)

      const restoResponse = await api.post(`/api/v2/auth/signup`, restoPayload)
      console.log("[Onboarding] Resto created response:", restoResponse)

      const createdResto =
        restoResponse?.resto ||
        restoResponse?.user?.resto ||
        restoResponse?.restaurant ||
        fallbackResto
      console.log("[Onboarding] Resto resolved from response:", createdResto)

      const resolvedPlaceId =
        createdResto?.place_id ||
        createdResto?.placeId ||
        restoResponse?.user?.place_id ||
        restoResponse?.user?.placeId ||
        fallbackResto.place_id
      console.log("[Onboarding] Resolved place_id for host signup:", resolvedPlaceId)

      const hostPayload = {
        name: host.name,
        lastName: host.lastName,
        email: form.hostEmail.trim(),
        resto: {
          ...fallbackResto,
          ...createdResto,
          place_id: resolvedPlaceId,
        },
        type: "host",
      }
      console.log("[Onboarding] Creating host with payload:", hostPayload)

      const hostResponse = await api.post(`/api/v2/auth/signup`, hostPayload)
      console.log("[Onboarding] Host created response:", hostResponse)
      if (hostResponse?.error === true) {
        throw new Error(hostResponse?.message || "No se pudo crear el host.")
      }
      console.log("[Onboarding] Redirecting to pending approval page")
      history.push("/resto-pending-approval")
    } catch (error) {
      console.error("[Onboarding] Flow failed:", error)
      if (String(error?.message || "").includes("lastName")) {
        toast.error("Falta el apellido del administrador. Ingresa nombre y apellido.")
      } else {
      toast.error(error?.message || "No se pudo completar el onboarding.")
      }
    } finally {
      console.log("[Onboarding] Submit finished")
      setLoading(false)
    }
  }

  return (
    <Container>
      <Brand type="resto" />
      <FormContainer>
        <Title>Onboarding restaurante</Title>
        <Paragraph>Confirma los datos recibidos antes de crear el restaurante.</Paragraph>

        <Field>
          <Label>Nombre y apellido del dueño del restaurante</Label>
          <Input
            value={form.adminName}
            onChange={(event) => handleChange("adminName", event.target.value)}
            placeholder="Ej: Luis Gómez"
            disabled={loading}
          />
        </Field>

        <Field>
          <Label>Email del dueño del restaurante</Label>
          <Input
            type="email"
            value={form.adminEmail}
            onChange={(event) => handleChange("adminEmail", event.target.value)}
            placeholder="Ej: luis@test.com"
            disabled
            readOnly
          />
        </Field>

        <Field>
          <Label>Nombre del restaurante</Label>
          <Input
            value={form.restaurantName}
            onChange={(event) => handleChange("restaurantName", event.target.value)}
            placeholder="Ej: Harry Sasson"
            disabled
            readOnly
          />
        </Field>

        <Field>
          <Label>Dirección del restaurante</Label>
          <Input
            value={form.restaurantAddress}
            onChange={(event) => handleChange("restaurantAddress", event.target.value)}
            placeholder="Ej: Cra 71 Bogotá"
            disabled
            readOnly
          />
        </Field>

        <Field>
          <Label>País del restaurante</Label>
          <Input
            value={form.restaurantCountry}
            onChange={(event) => handleChange("restaurantCountry", event.target.value)}
            placeholder="Ej: Colombia"
            disabled
            readOnly
          />
        </Field>

        {/*
        <Field>
          <Label>Nombre y apellido del host</Label>
          <Input
            value={form.hostFullName}
            onChange={(event) => handleChange("hostFullName", event.target.value)}
            placeholder="Ej: Juan Pérez"
            disabled={loading}
          />
        </Field>

        <Field>
          <Label>Email del host</Label>
          <Input
            type="email"
            value={form.hostEmail}
            onChange={(event) => handleChange("hostEmail", event.target.value)}
            placeholder="Ej: juan@test.com"
            disabled={loading}
          />
        </Field>
        */}

        <LegalConsent
          audience="resto"
          checked={acceptedLegal}
          onChange={setAcceptedLegal}
          disabled={loading}
        />
        <SubmitButton type="button" onClick={handleSubmit} disabled={loading}>
          {loading ? "Creando..." : "Confirmar"}
        </SubmitButton>
      </FormContainer>
    </Container>
  )
}

export default OnboardingPage

const Container = styled.div`
  padding: 20px;
`

const FormContainer = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 20px;
`

const Title = styled.h2`
  margin: 0 0 10px;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
`

const Paragraph = styled.p`
  margin: 0 0 24px;
  font-size: 14px;
  text-align: center;
  color: #666;
`

const FieldRow = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

const Field = styled.div`
  margin-bottom: 14px;
`

const Label = styled.label`
  display: block;
  text-align: left;
  font-weight: 500;
  margin: 0 0 6px 4px;
  font-size: 14px;
`

const Input = styled.input`
  width: 100%;
  height: 46px;
  border: 1px solid #ddd;
  border-radius: 10px;
  padding: 0 12px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;

  &:focus {
    border-color: #000;
  }
`

const theme = getHeaderTheme();

const SubmitButton = styled.button`
  border-radius: 100px;
  width: 100%;
  background: ${theme.primary};
  border: 2px solid transparent;
  color: ${theme.text};
  font-weight: 600;
  font-size: 15px;
  padding: 12px;
  margin-top: 20px;
  cursor: pointer;
  transition: filter 0.2s ease;

  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
